import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import React, { JSX, useContext, useEffect, useMemo } from "react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { studentData } from "../../../../context/studentContext";
import { ContentCopy } from "@mui/icons-material";
type Callback = (() => void) | null | undefined;
type ReturnCallback = (() => Promise<string | void> | string | void) | null | void;

type ButtonType = 'WARNING' | 'INFO' | null;
type ModalType = 'EXPIRE_PERSON' | 'EXPIRE_STUDENT' | 'DUPLICATE_FIELD' | 'REMOVE_ASSIGNMENT' | 'SAVED_CHANGES' | 'SAVED_ASSIGNMENTS' | 'REACTIVATE' | 'CANNOT_ARCHIVE_SELF' | 'CANNOT_ARCHIVE_HIGHER_USER' | 'NOT_ENOUGH_REFERENCES' | 'STUDENT_IS_EXPIRED' | 'NO_EMAIL_FOUND' | "GENERATING_LINK" | "LINK_PREPARED" | 'REMOTE_LAUNCH_INITIALIZED' | 'REMOTE_LAUNCH_SUCCESS' | "REMOTE_LAUNCH_FAILURE" | "REMOTE_LAUNCH_PARTIAL_SUCCESS" | 'PARENT_SEND_GENERAL_INFORMATION_SUCCESS' | "PARENTAL_CONSENT_LOCAL" | "PARENTAL_CONSENT_SENT" | "PARENTAL_CONSENT_SUCCESS" | "PARENTAL_CONSENT_ERROR" | "PARENTAL_CONSENT_PENDING" | 'TRUESAGE_INFORMATION_SENT' | "PARENTAL_CONSENT_NOT_GIVEN" | "TRIAGE_GENERATION_INITIALIZED" | 'UNREAD_TRIAGE_GENERATION_INITIALIZED' | 'TRIAGE_NO_SESSIONS_FOUND' | 'EDIT_IN_PROGRESS' | 'NO_COMPLETE_SESSIONS' | "USER_CAP" | "ROLE_PERMISSIONS_MISSING" | 'GENERAL_ERROR' | 'UNKNOWN' | null;
type ModalStatus = 'SAVING' | null;
type ModalReasonCloseType = "backdropClick" | "escapeKeyDown";

type ModalEventType = 'STANDARD' | 'DISABLE_CTA' | 'DISABLE_BACKDROP' | 'DISABLE_BACKDROP_AND_CTA' | null;

type ManageModalState = {
    id?: string | null,
    type: ModalType,
    status?: ModalStatus,
    message?: string | null,
    eventType?: ModalEventType
    students?: studentData[] | [],
    sessionLink?: string | null,
    onCancel?: ReturnCallback,
    onConfirm?: ReturnCallback,
    modalAction?: (() => void) | undefined,
    cancelButtonLabel?: string,
    confirmButtonLabel?: string,
    confirmButtonType?: ButtonType,
}

export type ManageModalContext = {
    id?: string | null,
    type: ModalType,
    status?: ModalStatus,
    message?: string | null,
    eventType?: ModalEventType,
    students?: studentData[] | [],
    sessionLink?: string | null,
    openModal: React.Dispatch<React.SetStateAction<ManageModalState>>,
    onCancel?: ReturnCallback,
    onConfirm?: ReturnCallback,
    modalAction?: (() => void) | undefined,
    cancelButtonLabel?: string,
    confirmButtonLabel?: string,
    confirmButtonType?: ButtonType,
    modalResult: ModalResultState | null,
    setModalResult: React.Dispatch<React.SetStateAction<ModalResultState | null>>,
}

export type ModalResultState = {
    [k: string]: {
        method: 'cancel' | 'close' | 'confirm',
        result: Promise<string | null | undefined | void> | string | null | undefined | void,
    }
}

export const ManageModalContext = React.createContext<ManageModalContext | null>(null);
ManageModalContext.displayName = "ManageModalContextProvider";

export const ManageModalProvider = ({ children }: { children: React.ReactNode }) => {

    const [state, openModal] = useState<ManageModalState>({
        id: null,
        type: null,
        status: null,
        message: null,
        students: [],
        sessionLink: null,
        onCancel: null,
        onConfirm: null,
        modalAction: undefined,
        eventType: "STANDARD",
        cancelButtonLabel: 'Cancel',
        confirmButtonLabel: 'Confirm'
    });

    const [modalResult, setModalResult] = useState<ModalResultState | null>(null);

    const memorizedValue = useMemo(() => ({
        ...state, openModal, modalResult, setModalResult
    }), [state, openModal, modalResult, setModalResult]);

    return (
        <ManageModalContext.Provider value={memorizedValue}>
            {children}
        </ManageModalContext.Provider>
    )
}

export const useManageModalContext = () => {
    const context = useContext(ManageModalContext);

    if (!context)
        throw new Error('Call made outside provider. You must use useManageModal within the ManageModalContext provider.');

    return context;
}

export const useManageModal = () => {
    const { type, modalResult, openModal } = useManageModalContext();

    const closeModal = () => openModal({ id: 'default', type: null });

    useEffect(() => {
        if (!modalResult) return;
        // console.log('Modal result!', modalResult);
    }, [modalResult]);

    return { type, modalResult, openModal, closeModal } as const;
}

const buttonStyle = {
    maxWidth: "155px",
    fontSize: "12px",
    height: "40px",
    marginTop: "16px",
    color: "#000000",
    background: "#C4C4C4",
    border: "1px solid #000000",
    "&:hover": {
        backgroundColor: "#c4c4c4",
    },
};

export default function ManageModal() {

    const {
        id = 'default',
        status,
        type,
        eventType = 'STANDARD',
        students,
        sessionLink,
        openModal,
        onCancel,
        onConfirm,
        modalAction,
        cancelButtonLabel,
        confirmButtonLabel,
        confirmButtonType,
        setModalResult
    } = useManageModalContext()

    const handleCloseModal = (reason: ModalReasonCloseType) => {
        // console.log('Backdrop close: ' + eventType);
        if (eventType === 'STANDARD' || eventType === 'DISABLE_CTA') {
            openModal({ id, type: null });
        }
    }

    const handleCancel = () => {
        openModal({ id, type: null })

        if (onCancel) {
            setModalResult(state => ({
                ...state,
                [id as string]: {
                    method: 'cancel',
                    result: onCancel()
                }
            }))
        }
    }

    const handleConfirm = () => {
        if (onConfirm) {
            setModalResult(state => ({
                ...state,
                [id as string]: {
                    method: 'confirm',
                    result: onConfirm()
                }
            }))
        }
    }


    return createPortal(
        <ManageModal.Wrapper open={Boolean(type)} onClose={handleCloseModal}>
            <ManageModal.Body>
                <ModalMessage type={type} students={students} sessionLink={sessionLink} modalAction={modalAction} />
                <ManageModal.Footer
                    status={status ?? null}
                    cancelButtonLabel={cancelButtonLabel}
                    confirmButtonLabel={confirmButtonLabel}
                    confirmButtonType={confirmButtonType}
                    onCancel={handleCancel}
                    onConfirm={!onConfirm ? undefined : handleConfirm}
                    showCta={['STANDARD', 'DISABLE_BACKDROP'].includes(eventType ?? 'STANDARD')} />
            </ManageModal.Body>
        </ManageModal.Wrapper>,
        document.body);
}

ManageModal.Wrapper = function (
    {
        open,
        onClose,
        children
    }: {
        open: boolean,
        onClose: (reason: ModalReasonCloseType) => void, children: React.ReactNode
    }
) {
    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="manage-modal"
            aria-describedby="manage-modal-description"
            sx={{ textAlign: "center" }}
        >
            <>
                {children}
            </>
        </Modal>
    )
}

ManageModal.Body = function ({ children }: { children: React.ReactNode }) {
    return (
        <Box component="div" sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            borderRadius: "10px",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            padding: "24px",
        }}>
            <>
                {children}
            </>
        </Box>
    )
}

ManageModal.Footer = function ({
    status,
    showCta,
    cancelButtonLabel,
    confirmButtonLabel,
    confirmButtonType,
    onCancel,
    onConfirm
}: {
    status: ModalStatus,
    showCta: boolean
    cancelButtonLabel?: string,
    confirmButtonLabel?: string,
    confirmButtonType?: ButtonType,
    onCancel: () => void,
    onConfirm: Callback
}) {
    return showCta ? (
        <Box
            component="div"
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-evenly",
            }}
        >
            {onConfirm && (
                <LoadingButton
                    loading={status === 'SAVING'}
                    sx={{
                        ...buttonStyle,
                        color: confirmButtonType === "WARNING" ? "#8E1C3B" : "#000000",
                    }}
                    variant="contained"
                    onClick={onConfirm}
                >
                    {confirmButtonLabel ?? 'Confirm'}
                </LoadingButton>
            )}
            <Button
                disabled={status === 'SAVING'}
                sx={buttonStyle}
                color="secondary"
                variant="contained"
                onClick={onCancel}
            >
                {cancelButtonLabel ?? 'Cancel'}
            </Button>{" "}
        </Box>
    ) : <></>;
}

function ModalMessage({ type, students, sessionLink, modalAction }: { type: ModalType, students: studentData[] | [] | undefined, sessionLink?: string | null, modalAction?: (() => void) | undefined }) {
    const messageComponents: Record<NonNullable<ModalType>, JSX.Element> = {
        'EXPIRE_PERSON': (
            <Typography>
                Expiring this record will mean the User will no longer have access. Do you wish to proceed?
            </Typography>
        ),
        'EXPIRE_STUDENT': (
            <Typography>
                Launching Tweenscreen will no longer be available for this child if Expired. Do you wish to proceed?
            </Typography>
        ),
        'DUPLICATE_FIELD': (
            <Typography>
                Custom field at this location with same type and name exists already.
            </Typography>
        ),
        'REMOVE_ASSIGNMENT': (
            <Typography>
                This will remove this User’s access (it will NOT expire the User’s record). Do you wish to proceed?
            </Typography>
        ),
        'SAVED_CHANGES': (
            <Typography>
                Successfully saved!
            </Typography>
        ),
        'SAVED_ASSIGNMENTS': (
            <Typography>
                Successfully saved!
            </Typography>
        ),
        'REACTIVATE': (
            <Typography>
                Please contact your futuresTHRIVE representative for assistance reactivating a User. Support@FuturesThrive.com
            </Typography>
        ),
        'CANNOT_ARCHIVE_SELF': (
            <Typography>
                You cannot archive your own account, please contact another admin or your futuresTHRIVE representative for assistance.
            </Typography>
        ),
        'CANNOT_ARCHIVE_HIGHER_USER': (
            <Typography>You do not have permission to archive this user, please contact your futuresTHRIVE representative for assistance</Typography>
        ),
        'NOT_ENOUGH_REFERENCES': (
            <Typography>
                This User cannot be removed (de-linked) because at least 1 Admin must be associated with a child record. Another Admin User must be linked before this User can be removed.
            </Typography>
        ),
        'NO_EMAIL_FOUND': (
            <div>
                <Typography>
                    There is no email on file for this child.
                </Typography>
                <Typography sx={{ padding: "8px 0px" }}>
                    Please have your Admin update the child’s profile to support the remote launch option.
                </Typography>
            </div>
        ),
        'STUDENT_IS_EXPIRED': (
            <div>
                <Typography>
                    This student's account is expired.
                </Typography>
                <Typography sx={{ padding: "8px 0px" }}>
                    Please have your Admin update the child’s profile to launch new sessions.
                </Typography>
            </div>
        ),
        "GENERATING_LINK": (
            <div>
                <Typography>
                    One minute we are sending the link.
                </Typography>
            </div>),
        "LINK_PREPARED": (
            <div>
                <Typography>
                    SUCCESS!
                </Typography>
                <Typography sx={{ padding: "8px 0px" }}>
                    Here is a link, copy and paste it so you can send it through your own system.
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        wordBreak: "break-all",
                        backgroundColor: "grey.100",
                        padding: "6px 10px",
                        borderRadius: "6px",
                        fontFamily: "monospace"
                    }}
                >
                    {sessionLink}
                </Typography>
                <Tooltip title="Copy">
                    <IconButton size="small" onClick={modalAction}>
                        <ContentCopy fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>),

        'REMOTE_LAUNCH_INITIALIZED': (
            <Typography>
                One minute we are sending the link
            </Typography>
        ),
        'REMOTE_LAUNCH_SUCCESS': (
            <div>
                <Typography>
                    SUCCESS!
                </Typography>
                <Typography sx={{ padding: "8px 0px" }}>
                    An email with the child's remote launch link was sent to the requested email on file.
                </Typography>
                <Typography sx={{ paddingBottom: "8px" }}>
                    The link is good for 2 weeks.
                </Typography>
            </div>
        ),
        'REMOTE_LAUNCH_FAILURE': (
            <div>
                <Typography>Ooops</Typography>
                <Typography>Something went wrong, please try again in a few minutes</Typography>
            </div>
        ),
        "REMOTE_LAUNCH_PARTIAL_SUCCESS": (
            <div>
                <Typography>Some emails were sent, there was an error with the following</Typography>
                <ul style={{ listStylePosition: "inside" }}>
                    {students?.map((student) => (
                        <li>{student.firstName} {student.lastName}</li>
                    ))}

                </ul>
            </div>
        ),
        'PARENT_SEND_GENERAL_INFORMATION_SUCCESS': (
            <div>
                <Typography>
                    SUCCESS!
                </Typography>
                <Typography sx={{ padding: "8px 0px" }}>
                    The general information email has been sent
                </Typography>
            </div>
        ),
        'PARENTAL_CONSENT_LOCAL': (
            <div>
                <Typography>SUCCESS!</Typography>
                <Typography>A new tab should open with a form for the parent to sign</Typography>
                <Typography>You can find a record of this in Summary Notes</Typography>
            </div>
        ),
        'PARENTAL_CONSENT_SENT': (
            <div>
                <Typography>SUCCESS!</Typography>
                <Typography sx={{ padding: "8px 0px" }}>
                    An email containing a link for parental consent was sent
                </Typography>
                <Typography>You can find a record of this in Summary Notes</Typography>
            </div>
        ),
        "PARENTAL_CONSENT_ERROR": (
            <div>
                <Typography>Error</Typography>
                <Typography sx={{ padding: "8px 0px" }}>
                    An error has occured, please try again in a moment
                </Typography>
            </div>
        ),
        'PARENTAL_CONSENT_SUCCESS': (
            <div>
                <Typography>Success!</Typography>
                <Typography sx={{ padding: "8px 0px" }}>
                    An email containing a copy of the completed consent form will be sent to you.
                </Typography>
            </div>
        ),
        'TRUESAGE_INFORMATION_SENT': (
            <div>
                <Typography>SUCCESS!</Typography>
                <Typography>An email containing the selected documents was sent</Typography>
            </div>
        ),
        "PARENTAL_CONSENT_PENDING": (
            <div>
                <Typography>Can't launch session</Typography>
                <Typography>Parental consent still pending</Typography>
            </div>),
        "PARENTAL_CONSENT_NOT_GIVEN": (
            <div>
                <Typography>Can't launch session</Typography>
                <Typography>Parent did not give their consent</Typography>
            </div>
        ),
        "TRIAGE_GENERATION_INITIALIZED": (
            <div>
                <Typography>
                    One minute, we are making the report
                </Typography>
            </div>
        ),
        "UNREAD_TRIAGE_GENERATION_INITIALIZED": (
            <div>
                <Typography>
                    One minute, we are making the report of the children in Actions Needed
                </Typography>
            </div>
        ),
        "TRIAGE_NO_SESSIONS_FOUND": (
            <div>
                <Typography>
                    Error, no completed sessions were found with the given infomration, please select a new search parameter.
                </Typography>
            </div>
        ),
        "EDIT_IN_PROGRESS": (
            <div>
                <Typography>One minute, we are making the update</Typography>
            </div>
        ),
        "NO_COMPLETE_SESSIONS": (
            <div><Typography>There are no completed sessions for this child</Typography></div>
        ),
        "USER_CAP": (
            <div><Typography>You have reached the cap for active users at your organization, please contact support@futuresthrive for assistance</Typography></div>
        ),
        "ROLE_PERMISSIONS_MISSING": (
            <div><Typography>You cannot perform this action due to your assigned role. Please contact your admin for help</Typography></div>
        ),

        'GENERAL_ERROR': (
            <div>
                <Typography sx={theme => ({ color: theme.palette.error.main, fontWeight: 'bold' })}>
                    Error!
                </Typography>
                <Typography sx={{ padding: "8px 0px" }}>
                    Something has gone wrong. The incident has been logged and sent to support. Please try again in a few.
                </Typography>
            </div>
        ),
        'UNKNOWN': (
            <Typography>
                Message type not recognized
            </Typography>
        )
    }

    return type
        ? messageComponents[type]
        : messageComponents['UNKNOWN'];
}